import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileSpreadsheet } from 'lucide-react';
import { useEstimateList } from './hooks';
import { isExpired } from './isExpired';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ListScreen, ListRow, type ListTab } from '../../components/ListScreen';

const TABS: ListTab[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'closed', label: 'Closed' }
];

const STATUS_BADGE: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600 border-slate-200',
  Sent: 'bg-slate-100 text-ink border-slate-200',
  Accepted: 'bg-green-50 text-green-800 border-green-200',
  Declined: 'bg-red-50 text-red-800 border-red-200',
  Converted: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  Void: 'bg-red-50 text-red-800 border-red-200',
  Expired: 'bg-amber-50 text-amber-800 border-amber-200'
};

function inTab(status: string, expired: boolean, tab: string): boolean {
  if (tab === 'all') return true;
  if (tab === 'open') return !expired && (status === 'Draft' || status === 'Sent');
  if (tab === 'accepted') return status === 'Accepted';
  if (tab === 'closed') return expired || status === 'Declined' || status === 'Void' || status === 'Converted';
  return true;
}

export function EstimateListPage() {
  usePageTitle('Estimates');
  const [params] = useSearchParams();
  const customerId = params.get('customerId') ?? undefined;
  const [tab, setTab] = useState('all');

  // Fetch a wide page; filter by tab client-side (Expired is derived, so tabs
  // can't be a server status filter — and B2 adds no API params).
  const { data, isLoading } = useEstimateList({ customerId, page: 1, pageSize: 100 });
  const rows = (data?.items ?? []).filter((e) => inTab(e.status, isExpired(e.status, e.expiresAtUtc), tab));

  return (
    <ListScreen
      title="Estimates"
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      primaryAction={{ label: 'New Estimate', to: '/estimates/new' }}
      isEmpty={!isLoading && rows.length === 0}
      empty={{ icon: <FileSpreadsheet size={40} />, message: 'No estimates here yet.' }}
    >
      {isLoading ? (
        <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>
      ) : (
        rows.map((e) => {
          const expired = isExpired(e.status, e.expiresAtUtc);
          const label = expired ? 'Expired' : e.status;
          return (
            <ListRow
              key={e.id}
              to={`/estimates/${e.id}`}
              primary={e.customerName}
              badge={
                <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] ${STATUS_BADGE[label] ?? ''}`}>
                  {label}
                </span>
              }
              secondary={`${e.estimateNumber}${e.expiresAtUtc ? ` · valid until ${new Date(e.expiresAtUtc).toLocaleDateString()}` : ''}`}
              right={`${e.total.toFixed(2)} ${e.currency}`}
              rightSub={new Date(e.issuedAtUtc).toLocaleDateString()}
            />
          );
        })
      )}
    </ListScreen>
  );
}
